import { JitsiMeeting } from "@jitsi/react-sdk";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link2 } from "lucide-react";
import type { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";

import LoadingScreen from "@/components/shared/LoadingScreen";
import { Button } from "@/components/primitives/button";
import { useToast } from "@/hooks/useToast";
import { copyTextToClipboard } from "@/lib/clipboard";
import { mintMeetingToken } from "@/server/services/meetingsService";
import { MintMeetingTokenRequest } from "@/server/types/umbra/meet";
import { ApiError } from "@/server/apiError";
import { useCallModerationState } from "@/zustand/CallModerationState";
import {
  buildMeetToolbarButtons,
  buildOrionCustomToolbarButtons,
  buildOrionMainToolbarButtons,
} from "@/components/chat/middle-column/call/jitsiToolbar";
import useModerationEvents from "@/components/chat/middle-column/call/useModerationEvents";
import useModerationButtonEvent from "@/components/chat/middle-column/call/useModerationButtonEvent";
import useModerationButtonVisibility from "@/components/chat/middle-column/call/useModerationButtonVisibility";
import useRecordingConsentToast from "@/components/chat/middle-column/call/useRecordingConsentToast";
import ModerationPanel from "@/components/chat/middle-column/call/ModerationPanel";

type JoinState =
  | { status: "loading" }
  | { status: "error"; notFound: boolean }
  | {
      status: "joined";
      token: string;
      room: string;
      subject: string;
      /**
       * Monotonic conference generation (see `generationRef`) — identifies
       * WHICH conference this joined state belongs to, so stale async events
       * (late mint resolutions, leaked `readyToClose` fires) can never act on
       * a newer conference. Unlike an `identity#attempt` key, it never repeats
       * within the page's lifetime (an ABA route cycle A → B → A reuses the
       * SAME key but gets a NEW generation).
       */
      generation: number;
    }
  | { status: "left" };

export interface MeetConferenceProps {
  /**
   * The token-mint request — `{ meetingId }` (standalone join) XOR `{ alias }`
   * (vanity join). Read through a ref so an inline-literal prop does not retrigger
   * the mint effect; the effect keys off `identity` instead.
   */
  mintRequest: MintMeetingTokenRequest;
  /**
   * Stable identity for THIS conference — the meetingId or the canonical alias.
   * Drives the StrictMode/ABA mint-dedup key and participates in the Jitsi
   * remount key. Two different identities MUST get two different mounts.
   */
  identity: string;
  /** Shareable link copied by the built-in copy button (in-call + left state). */
  shareLink: string;
  /** Loading-screen title (defaults to the generic "joining" copy). */
  loadingTitle?: string;
  /** Not-found copy override (vanity uses alias-specific wording). */
  notFoundTitle?: string;
  notFoundDescription?: string;
  /**
   * Optional owner-only in-call actions (e.g. the standalone "copy guest link"
   * button), rendered beside the copy-link button and given the minted token so
   * the caller can gate on `readModeratorClaim`.
   */
  renderOwnerActions?: (ctx: { token: string }) => React.ReactNode;
}

/**
 * Shared standalone-meeting CONFERENCE surface — the mint state machine +
 * StrictMode/ABA generation guards + Orion moderation stack + Jitsi embed +
 * loading/error/left screens, extracted from `MeetJoinPage` so the vanity join
 * (`VanityMeetJoin`, keyed on an alias) reuses IDENTICAL behavior instead of
 * forking it. `MeetJoinPage`'s existing suite is the behavior oracle for this
 * extraction (it must stay green with zero edits).
 *
 * The BACKEND decides the role: the owner mints a moderator token, everyone
 * else a member token — there is deliberately NO owner/member branch here.
 * Unknown/expired meetings (or an unclaimed/unknown alias) surface as a generic
 * 404 → one clean not-found state (no reason leak).
 *
 * TOOLBAR PARITY + StrictMode iframe caveat: see `MeetJoinPage`'s original doc
 * comment — this component carries that behavior verbatim.
 */
export default function MeetConference({
  mintRequest,
  identity,
  shareLink,
  loadingTitle,
  notFoundTitle,
  notFoundDescription,
  renderOwnerActions,
}: MeetConferenceProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, setState] = useState<JoinState>({ status: "loading" });
  // Bumped by Rejoin/Try-again — re-runs the mint effect AND remounts the embed.
  const [attempt, setAttempt] = useState(0);

  // Latest mintRequest, read inside the mint effect so an inline-literal prop
  // does not have to sit in the effect deps (which would refire every render).
  const mintRequestRef = useRef(mintRequest);
  mintRequestRef.current = mintRequest;

  // The live external API for THIS embed. LOCAL state — this component never
  // reads or writes `CallState.apiRef`, so it can neither inherit a stale api
  // leaked by a previous in-chat call nor clobber a live one. Cleared whenever
  // a new mint starts so the hooks below detach from the OLD conference.
  const [api, setApi] = useState<IJitsiMeetExternalApi | null>(null);
  const isModerationPanelOpen = useCallModerationState(
    (s) => s.isModerationPanelOpen,
  );
  const setModerationPanelOpen = useCallModerationState(
    (s) => s.setModerationPanelOpen,
  );

  // Mint exactly once per (identity, attempt) — StrictMode's dev double-mount
  // must not double-POST (the endpoint is rate-limited).
  const mintedForRef = useRef<string | null>(null);
  // Monotonic CONFERENCE GENERATION — bumped once per real mint. This (not the
  // `identity#attempt` key) is what stale async work is checked against: the key
  // REPEATS across an ABA route cycle (A → B → A, all attempt 0 — the component
  // stays mounted across identity changes, so refs survive), which would let A's
  // leftovers act on the NEW A conference. The generation never repeats.
  const generationRef = useRef(0);
  useEffect(() => {
    if (!identity) {
      setState({ status: "error", notFound: true });
      return;
    }
    const key = `${identity}#${attempt}`;
    if (mintedForRef.current === key) {
      return;
    }
    mintedForRef.current = key;
    const generation = ++generationRef.current;
    setState({ status: "loading" });
    // Detach from the previous conference's api NOW — not at onApiReady of the
    // next one — so the hooks below never briefly command the OLD conference.
    setApi(null);

    (async () => {
      try {
        const { token, room, subject } = await mintMeetingToken(
          mintRequestRef.current,
        );
        if (generationRef.current !== generation) {
          return; // superseded by a newer mint (incl. an ABA cycle back here)
        }
        setState({ status: "joined", token, room, subject, generation });
      } catch (error) {
        if (generationRef.current !== generation) {
          return;
        }
        // requestApi maps the BE problem+json to ApiError carrying the HTTP
        // status — 404 is the backend's generic "invalid or does not exist".
        const notFound = error instanceof ApiError && error.status === 404;
        setState({ status: "error", notFound });
      }
    })();
  }, [identity, attempt]);

  // ── Leave handling ── (STALE-FIRE GUARD, same landmine as Call.tsx: the SDK
  // wires the listener ONCE and never disposes the external API, so a leaked old
  // embed can fire a late readyToClose. Each handler closes over the GENERATION
  // it was wired for and only marks THAT conference as left.)
  const joinedGeneration = state.status === "joined" ? state.generation : null;
  const handleReadyToClose = useCallback(() => {
    setState((prev) =>
      prev.status === "joined" && prev.generation === joinedGeneration
        ? { status: "left" }
        : prev,
    );
  }, [joinedGeneration]);

  // Wire the Orion moderation stack to THIS embed only while joined. Every hook
  // no-ops on null. `useModerationEvents` also resets the moderation store when
  // the api instance changes, so state never leaks across meetings/calls.
  const meetApi = state.status === "joined" ? api : null;
  useModerationEvents(meetApi);
  useModerationButtonEvent(meetApi);
  const orionToolbarLabels = useMemo(
    () => ({
      chat: t("videoCall.orionChat.togglePanel"),
      moderation: t("videoCall.moderation.panelTitle"),
      minimize: t("videoCall.minimize"),
    }),
    [t],
  );
  useModerationButtonVisibility(meetApi, orionToolbarLabels, {
    includeChat: false,
    includeMinimize: false,
  });
  useRecordingConsentToast(meetApi);

  const subject = state.status === "joined" ? state.subject : undefined;
  const configOverwrite = useMemo(
    () => ({
      subject,
      enableNoisyMicDetection: true,
      enableNoAudioDetection: true,
      disableInviteFunctions: true,
      disableProfile: true,
      videoQuality: { minHeightForQualityLvl: { 0: "high" } },
      customToolbarButtons: buildOrionCustomToolbarButtons(orionToolbarLabels, {
        includeChat: false,
        includeMinimize: false,
      }),
      mainToolbarButtons: buildOrionMainToolbarButtons("chat", "fullscreen"),
    }),
    [subject, orionToolbarLabels],
  );
  const interfaceConfigOverwrite = useMemo(
    () => ({ TOOLBAR_BUTTONS: buildMeetToolbarButtons(true) }),
    [],
  );

  async function handleCopyLink() {
    const ok = await copyTextToClipboard(shareLink);
    toast({
      duration: 2000,
      description: ok
        ? t("standaloneMeet.copySuccess")
        : t("standaloneMeet.copyFailed"),
      ...(ok ? {} : { variant: "destructive" as const }),
    });
  }

  if (state.status === "loading") {
    return (
      <LoadingScreen title={loadingTitle ?? t("standaloneMeet.joining")} />
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-background p-6 text-center font-DMSans text-foreground">
        <h1 className="text-xl font-semibold">
          {state.notFound
            ? (notFoundTitle ?? t("standaloneMeet.notFoundTitle"))
            : t("standaloneMeet.joinFailedTitle")}
        </h1>
        <p className="max-w-md text-sm opacity-80">
          {state.notFound
            ? (notFoundDescription ?? t("standaloneMeet.notFoundDescription"))
            : t("standaloneMeet.joinFailedDescription")}
        </p>
        {!state.notFound && (
          <Button onClick={() => setAttempt((a) => a + 1)}>
            {t("standaloneMeet.retry")}
          </Button>
        )}
      </div>
    );
  }

  if (state.status === "left") {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-background p-6 text-center font-DMSans text-foreground">
        <h1 className="text-xl font-semibold">
          {t("standaloneMeet.leftTitle")}
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => setAttempt((a) => a + 1)}>
            {t("standaloneMeet.rejoin")}
          </Button>
          <Button variant="secondary" onClick={handleCopyLink}>
            {t("standaloneMeet.copyLink")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/", { replace: true })}
          >
            {t("standaloneMeet.backHome")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-dvh w-full bg-black">
      {/* Copy-link pill — the same floating-overlay slot Call.tsx uses. */}
      <div className="absolute z-[52] m-2 flex items-center gap-2 rounded-sm border border-primary/80 bg-black/40">
        <button
          type="button"
          aria-label={t("standaloneMeet.copyLink")}
          title={t("standaloneMeet.copyLink")}
          onClick={handleCopyLink}
          className="p-1 text-white hover:bg-primary/50"
        >
          <Link2 className="size-6" aria-hidden="true" />
        </button>
        {renderOwnerActions?.({ token: state.token })}
      </div>
      {isModerationPanelOpen && (
        <ModerationPanel
          onClose={() => setModerationPanelOpen(false)}
          apiOverride={meetApi}
        />
      )}
      <JitsiMeeting
        // KEYED remount per conference generation: @jitsi/react-sdk never
        // reloads the iframe on prop changes, so an identity change or a Rejoin
        // MUST get a fresh mount. The generation subsumes room+attempt AND never
        // repeats across an ABA cycle.
        key={`${state.room}#${state.generation}`}
        domain={
          import.meta.env.VITE_MEET_DOMAIN || "meet.test.orionmessenger.io"
        }
        roomName={state.room}
        jwt={state.token}
        configOverwrite={configOverwrite}
        interfaceConfigOverwrite={interfaceConfigOverwrite}
        getIFrameRef={(parentNode) => {
          if (parentNode) {
            parentNode.style.width = "100%";
            parentNode.style.height = "100%";
          }
        }}
        onApiReady={(readyApi: IJitsiMeetExternalApi) => {
          // LOCAL only — deliberately NOT mirrored into CallState.apiRef.
          setApi(readyApi);
        }}
        onReadyToClose={handleReadyToClose}
      />
    </div>
  );
}
